/**
 * WLSN Options Selector Component
 *
 * Custom accessible dropdown/select component for option picking (works like a native <select>).
 *
 * Features:
 * - Fully keyboard-accessible (Tab, Enter/Space, Arrow keys, Esc for close)
 * - Syncs selection to hidden <input type="hidden"> for form integration
 * - Supports custom option labels, icons, and slotting extra HTML inside options
 * - Supports both controlled and uncontrolled usage (external JS can set value)
 * - Flexible for selling plans, subscriptions, variants, or any list of options
 * - Handles dynamic option updates via MutationObserver
 * - Emits native 'change' event on value change, plus custom events: wlsn:selector:open, wlsn:selector:close, wlsn:selector:change
 * - Customizable styles through CSS variables for seamless theme integration
 * - Group sync via data-selector-id (or id as fallback)
 * - Multiple wlsn-options-selector instances can share the same selector-id
 * - Only one in the group needs to have <input type="hidden"> – that becomes the "source of truth"
 *
 * HTML Structure Example:
 * <wlsn-options-selector data-selector-id="selling-plan">
 *   <input type="hidden" name="selling_plan" value="value_1">
 *   <wlsn-options-selector-button>
 *     <div class="wlsn-options-selector-label js-options-selector-label">
 *       <span>Subscription Dropdown</span>
 *     </div>
 *     <div class="wlsn-options-selector-placeholder js-options-selector-placeholder hidden"></div>
 *     <span class="wlsn-options-selector-icon">
 *       <!-- Optional: Custom icon (SVG or shopify icon render) -->
 *     </span>
 *   </wlsn-options-selector-button>
 *   <wlsn-options-selector-list
 *     role="listbox"
 *     aria-label="Subscription Dropdown"
 *     aria-hidden="true">
 *     <wlsn-options-selector-item
 *       role="option"
 *       data-option-value="test_value_1"
 *       tabindex="0">
 *       <span>Item 1</span>
 *     </wlsn-options-selector-item>
 *     <wlsn-options-selector-item ...> ... </wlsn-options-selector-item>
 *     <!-- ... more items -->
 *   </wlsn-options-selector-list>
 * </wlsn-options-selector>
 *
 * Styling: see assets/wlsn-options-selector.css for customizable variables and class hooks.
 *
 * Usage:
 * - Place anywhere in your layout where you need a form-friendly selector (e.g. for selling plans, sizes).
 * - Responds to changes like a native input for easy form handling.
 *
 * Events:
 * - Listen for "change" or "wlsn:selector:change" on the element to react to selection changes.
 */

if (!customElements.get('wlsn-options-selector')) {
  class WLSNOptionsSelector extends HTMLElement {
    // Globalni registry instanci po sync ID-ju
    static instancesBySyncId = new Map();

    constructor() {
      super();

      this.classes = {
        active: 'active',
        selected: 'selected',
        hidden: 'hidden',
        disabled: 'disabled',
        loadingState: 'wlsn-loading-state',
        optionsSelectorButton: 'wlsn-options-selector-button',
        optionsSelectorList: 'wlsn-options-selector-list',
        optionsSelectorItem: 'wlsn-options-selector-item',
        optionsSelectorLabel: 'js-options-selector-label',
        optionsSelectorPlaceholder: 'js-options-selector-placeholder',
      };

      this.isOpen = false;
      this.observer = null;
      this.syncId = null;

      // Bind all methods that will be used as event listeners
      this.boundMethods = {
        handleGlobalKeydown: this._handleGlobalKeydown.bind(this),
        handleGlobalClick: this._handleGlobalClick.bind(this),
        handleKeyboardNavigation: this._handleKeyboardNavigation.bind(this),
        handleButtonClick: this._handleButtonClick.bind(this),
      };

      // Store references to event listeners so we can remove them later
      this.itemEventListeners = new Map();
    }

    /**
     * Get DOM elements
     * @returns {object} - Object containing DOM element references
     */
    get elements() {
      return {
        optionsSelectorButton: this.querySelector(this.classes.optionsSelectorButton),
        optionsList: this.querySelector(this.classes.optionsSelectorList),
        optionsItems: this.querySelectorAll(this.classes.optionsSelectorItem),
        optionsPlaceholder: this.querySelector(`.${this.classes.optionsSelectorPlaceholder}`),
        optionsLabel: this.querySelector(`.${this.classes.optionsSelectorLabel}`),
        hiddenInput: this.querySelector('input[type="hidden"]'),
      };
    }

    /**
     * Connected callback lifecycle method
     * @returns {void}
     */
    connectedCallback() {
      // Determine sync ID (group identifier)
      this.syncId = this.getAttribute('data-selector-id') || this.getAttribute('id') || null;
      if (this.syncId) {
        if (!WLSNOptionsSelector.instancesBySyncId.has(this.syncId)) {
          WLSNOptionsSelector.instancesBySyncId.set(this.syncId, new Set());
        }
        WLSNOptionsSelector.instancesBySyncId.get(this.syncId).add(this);
      }

      this._setupEventListeners();
      this._setupMutationObserver();
      this._updatePlaceholderFromSelectedItem();

      // Add global event listeners
      document.addEventListener('keydown', this.boundMethods.handleGlobalKeydown);
      document.addEventListener('click', this.boundMethods.handleGlobalClick);
    }

    /**
     * Disconnected callback lifecycle method
     * @returns {void}
     */
    disconnectedCallback() {
      // Remove from registry
      if (this.syncId && WLSNOptionsSelector.instancesBySyncId.has(this.syncId)) {
        const group = WLSNOptionsSelector.instancesBySyncId.get(this.syncId);
        group.delete(this);
        if (group.size === 0) {
          WLSNOptionsSelector.instancesBySyncId.delete(this.syncId);
        }
      }

      // Clean up mutation observer if it exists
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }

      // Remove global event listeners if they exist
      document.removeEventListener('keydown', this.boundMethods.handleGlobalKeydown);
      document.removeEventListener('click', this.boundMethods.handleGlobalClick);

      // Remove all item event listeners if they exist
      this._cleanupItemEventListeners();

      // Remove component event listeners if they exist
      const { optionsSelectorButton } = this.elements;
      if (optionsSelectorButton) {
        optionsSelectorButton.removeEventListener('click', this.boundMethods.handleButtonClick);
      }

      this.removeEventListener('keydown', this.boundMethods.handleKeyboardNavigation);
    }

    /**
     * Setup mutation observer to watch for class changes
     * @returns {void}
     */
    _setupMutationObserver() {
      const { optionsItems } = this.elements;
      if (!optionsItems.length) return;

      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
            this._updatePlaceholderFromSelectedItem();
          }
        });
      });

      optionsItems.forEach((item) => {
        this.observer.observe(item, {
          attributes: true,
          attributeFilter: ['class'],
        });
      });
    }

    /**
     * Setup event listeners for selector and items
     * @returns {void}
     */
    _setupEventListeners() {
      const { optionsSelectorButton, optionsItems } = this.elements;

      if (optionsSelectorButton) {
        // Toggle options on button click
        optionsSelectorButton.addEventListener('click', this.boundMethods.handleButtonClick);
      }

      // Handle clicks on options items
      optionsItems.forEach((item) => {
        const clickHandler = (e) => {
          e.stopPropagation();
          this._handleItemInteraction(item);
        };

        item.addEventListener('click', clickHandler);
        this.itemEventListeners.set(item, { click: clickHandler });

        // Handle any input changes within items
        const input = item.querySelector('input');
        if (input) {
          const changeHandler = (e) => {
            e.stopPropagation();
            this._closeOptions();
          };

          input.addEventListener('change', changeHandler);
          const listeners = this.itemEventListeners.get(item);
          listeners.inputChange = changeHandler;
          this.itemEventListeners.set(item, listeners);
        }
      });

      // Handle keyboard navigation within the component
      this.addEventListener('keydown', this.boundMethods.handleKeyboardNavigation);
    }

    /**
     * Cleanup all item event listeners
     * @returns {void}
     */
    _cleanupItemEventListeners() {
      this.itemEventListeners.forEach((listeners, item) => {
        if (listeners.click) {
          item.removeEventListener('click', listeners.click);
        }

        const input = item.querySelector('input');
        if (input && listeners.inputChange) {
          input.removeEventListener('change', listeners.inputChange);
        }
      });

      this.itemEventListeners.clear();
    }

    /**
     * Handle button click event
     * @param {Event} e - The click event
     * @returns {void}
     */
    _handleButtonClick(e) {
      e.stopPropagation();
      this._toggleOptions();
    }

    /**
     * Handle global click event (outside of component)
     * @param {Event} e - The click event
     * @returns {void}
     */
    _handleGlobalClick(e) {
      if (!this.contains(e.target)) {
        this._closeOptions();
      }
    }

    /**
     * Update placeholder with selected item content
     * @returns {void}
     */
    _updatePlaceholderFromSelectedItem() {
      const { optionsPlaceholder, optionsLabel } = this.elements;
      const selectedItem = this.querySelector(`${this.classes.optionsSelectorItem}.${this.classes.selected}`);

      if (selectedItem && optionsPlaceholder) {
        const clonedContent = selectedItem.cloneNode(true);

        optionsPlaceholder.innerHTML = '';
        optionsPlaceholder.innerHTML = clonedContent.innerHTML;

        if (optionsLabel) {
          optionsLabel.classList.add(this.classes.hidden);
        }
        optionsPlaceholder.classList.remove(this.classes.hidden);
      } else {
        if (optionsLabel) {
          optionsLabel.classList.remove(this.classes.hidden);
        }
        if (optionsPlaceholder) {
          optionsPlaceholder.classList.add(this.classes.hidden);
        }
      }
    }

    /**
     * Toggle options dropdown open/closed
     * @returns {void}
     */
    _toggleOptions() {
      if (this.hasAttribute('disabled') || this.classList.contains(this.classes.disabled)) {
        return;
      }
      this.isOpen ? this._closeOptions() : this._openOptions();
    }

    /**
     * Core value selection (centralized location)
     * @param {string} value
     * @param {{ emit?: boolean, fromSync?: boolean }} options
     */
    _selectValue(value, { emit = true, fromSync = false } = {}) {
      const { optionsItems, hiddenInput } = this.elements;

      if (!optionsItems.length) return;

      let matchedItem = null;

      optionsItems.forEach((item) => {
        const itemValue = item.getAttribute('data-option-value') || item.getAttribute('data-value');
        if (itemValue === value) {
          item.classList.add(this.classes.selected);
          matchedItem = item;
        } else {
          item.classList.remove(this.classes.selected);
        }
      });

      if (!matchedItem) return;

      // Update hidden input if it exists
      if (hiddenInput && hiddenInput.value !== value) {
        hiddenInput.value = value;
        hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Update placeholder
      this._updatePlaceholderFromSelectedItem();

      // Emit custom event only from "original" instance (not from synced)
      if (emit) {
        window.WLSNHelpers.triggerEvent(this, 'wlsn:selector:change', {
          value,
          selectedItem: matchedItem,
          dataAttributes: this._getItemDataAttributes(matchedItem),
          fromSync,
        });
      }
    }

    /**
     * Handle item interaction (click)
     * @param {HTMLElement} item - The clicked item
     * @returns {void}
     */
    _handleItemInteraction(item) {
      const itemValue = item.getAttribute('data-option-value') || item.getAttribute('data-value');
      if (!itemValue) return;

      // 1) Primary selection in this instance (emits event)
      this._selectValue(itemValue, { emit: true, fromSync: false });

      // 2) Sync to other instances in the same group (without emitting)
      this._syncGroupSelection(itemValue);

      // 3) Close dropdown
      this._closeOptions();
    }

    /**
     * Sync selection to all instances with the same syncId
     * @param {string} value
     */
    _syncGroupSelection(value) {
      if (!this.syncId) return;

      const group = WLSNOptionsSelector.instancesBySyncId.get(this.syncId);
      if (!group) return;

      group.forEach((instance) => {
        if (instance === this) return; // skip self
        instance._selectValue(value, { emit: false, fromSync: true });
      });
    }

    /**
     * Get all data attributes from an item
     * @param {HTMLElement} item - The item element
     * @returns {object} - Object containing all data attributes
     */
    _getItemDataAttributes(item) {
      const dataAttributes = {};
      Array.from(item.attributes).forEach((attr) => {
        if (attr.name.startsWith('data-')) {
          const key = attr.name.slice(5).replace(/-([a-z])/g, (g) => g[1].toUpperCase());
          dataAttributes[key] = attr.value;
        }
      });
      return dataAttributes;
    }

    /**
     * Set disabled state (public method)
     * @param {boolean} disabled - Whether to disable the selector
     * @returns {void}
     */
    setDisabledState(disabled) {
      this.classList.toggle(this.classes.disabled, disabled);
      if (disabled) {
        this.setAttribute('disabled', '');
      } else {
        this.removeAttribute('disabled');
      }
    }

    removeDisabledState() {
      this.setDisabledState(false);
    }

    /**
     * Set loading state (public method)
     * @param {boolean} loading - Whether to show loading state
     * @returns {void}
     */
    setLoadingState(loading) {
      this.classList.toggle(this.classes.loadingState, loading);
    }

    removeLoadingState() {
      this.setLoadingState(false);
    }

    /**
     * Close options dropdown
     * @returns {void}
     */
    _closeOptions() {
      const { optionsList } = this.elements;
      if (!optionsList) return;

      this.isOpen = false;
      this.classList.remove(this.classes.active);
      optionsList.classList.remove(this.classes.active);
      optionsList.setAttribute('aria-hidden', 'true');
    }

    /**
     * Focus next item in dropdown
     * @returns {void}
     */
    _focusNextItem() {
      const focusableElements = this._getFocusableElements();
      const currentIndex = focusableElements.indexOf(document.activeElement);
      const nextIndex = (currentIndex + 1) % focusableElements.length;
      focusableElements[nextIndex].focus();
    }

    /**
     * Handle global keydown event (ESC key)
     * @param {KeyboardEvent} event - The keyboard event
     * @returns {void}
     */
    _handleGlobalKeydown(event) {
      if (!this.isOpen) return;

      const { optionsSelectorButton } = this.elements;

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this._closeOptions();
        optionsSelectorButton?.focus();
      }
    }

    /**
     * Handle keyboard navigation within dropdown
     * @param {KeyboardEvent} event - The keyboard event
     * @returns {void}
     */
    _handleKeyboardNavigation(event) {
      if (this.hasAttribute('disabled') || this.classList.contains(this.classes.disabled)) {
        return;
      }

      if (!this.isOpen) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this._openOptions();
        }
        return;
      }

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this._focusNextItem();
          break;
        case 'ArrowUp':
          event.preventDefault();
          this._focusPreviousItem();
          break;
      }
    }

    /**
     * Open options dropdown
     * @returns {void}
     */
    _openOptions() {
      if (this.hasAttribute('disabled') || this.classList.contains(this.classes.disabled)) {
        return;
      }

      const { optionsList } = this.elements;
      if (!optionsList) return;

      this.isOpen = true;
      this.classList.add(this.classes.active);
      optionsList.classList.add(this.classes.active);
      optionsList.setAttribute('aria-hidden', 'false');

      const firstInteractive = optionsList.querySelector('input, a, button, [tabindex]');
      if (firstInteractive) {
        firstInteractive.focus();
      }
    }

    /**
     * Focus previous item in dropdown
     * @returns {void}
     */
    _focusPreviousItem() {
      const focusableElements = this._getFocusableElements();
      const currentIndex = focusableElements.indexOf(document.activeElement);
      const previousIndex = (currentIndex - 1 + focusableElements.length) % focusableElements.length;
      focusableElements[previousIndex].focus();
    }

    /**
     * Get focusable elements in dropdown
     * @returns {Array<HTMLElement>} - Array of focusable elements
     */
    _getFocusableElements() {
      return Array.from(this.querySelectorAll('wlsn-options-selector-item[tabindex], input, a, button'));
    }
  }

  window.customElements.define('wlsn-options-selector', WLSNOptionsSelector);
}
